const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const REDUCE_MOTION = reduceMotion;
export const MOVE_MS = reduceMotion ? 80 : 650;
export const SPIN_MS = reduceMotion ? 80 : 850;

// Icon geometry as plain data: [tagName, attributes][]. The diagram is built
// with imperative DOM calls (see InfraDemo.js), so icons are assembled via
// createElementNS rather than JSX or innerHTML.
export const ICON_DEFS = {
  computer: [
    ['rect', { x: 2, y: 3, width: 20, height: 14, rx: 2 }],
    ['line', { x1: 8, y1: 21, x2: 16, y2: 21 }],
    ['line', { x1: 12, y1: 17, x2: 12, y2: 21 }]
  ],
  chip: [
    ['rect', { x: 7, y: 7, width: 10, height: 10, rx: 1 }],
    ['line', { x1: 7, y1: 10, x2: 4, y2: 10 }],
    ['line', { x1: 7, y1: 14, x2: 4, y2: 14 }],
    ['line', { x1: 17, y1: 10, x2: 20, y2: 10 }],
    ['line', { x1: 17, y1: 14, x2: 20, y2: 14 }],
    ['line', { x1: 10, y1: 7, x2: 10, y2: 4 }],
    ['line', { x1: 14, y1: 7, x2: 14, y2: 4 }],
    ['line', { x1: 10, y1: 17, x2: 10, y2: 20 }],
    ['line', { x1: 14, y1: 17, x2: 14, y2: 20 }]
  ],
  tool: [
    [
      'path',
      {
        d: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'
      }
    ]
  ],
  cloud: [['path', { d: 'M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z' }]],
  layers: [
    ['path', { d: 'M12 2 2 7l10 5 10-5-10-5z' }],
    ['path', { d: 'M2 17l10 5 10-5' }],
    ['path', { d: 'M2 12l10 5 10-5' }]
  ],
  shield: [['path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }]],
  book: [
    ['path', { d: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20' }],
    ['path', { d: 'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z' }]
  ],
  save: [
    ['path', { d: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z' }],
    ['polyline', { points: '17 21 17 13 7 13 7 21' }],
    ['polyline', { points: '7 3 7 8 15 8' }]
  ],
  database: [
    ['ellipse', { cx: 12, cy: 5, rx: 8, ry: 3 }],
    ['path', { d: 'M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5' }],
    ['path', { d: 'M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3' }]
  ],
  code: [
    ['polyline', { points: '16 18 22 12 16 6' }],
    ['polyline', { points: '8 6 2 12 8 18' }]
  ],
  mail: [
    ['rect', { x: 2, y: 4, width: 20, height: 16, rx: 2 }],
    ['path', { d: 'M2 6l10 7 10-7' }]
  ]
};

// ---- Scenario configs ----
// Each scenario defines nodes (x/y as percentages of the diagram track),
// optional groupBoxes (dashed/solid rectangles drawn around named member
// nodes), and a path — an ordered list of animation steps interpreted by
// the playback engine in InfraDemo.js (move / spin / merge / hold).
export const SCENARIOS = {
  simple: {
    caption: 'The user determines what the model does.',
    userMessage: 'Hello',
    response: 'Hello, how can I help you today?',
    nodes: {
      computer: { x: 15, y: 50, icon: 'computer', label: 'User' },
      llm: { x: 85, y: 50, icon: 'chip', label: 'LLM' }
    },
    start: 'computer',
    path: [
      { type: 'move', to: 'llm' },
      { type: 'move', to: 'computer' }
    ]
  },
  'assistant-system-prompt': {
    caption: 'The developer determines the workflow.',
    userMessage: 'Hello',
    response:
      "Hello. Let's think about this through a supply chain lens — what's the trade-off between cost, speed, and risk here?",
    nodes: {
      computer: { x: 8, y: 50, icon: 'computer', label: 'User' },
      llm: { x: 92, y: 50, icon: 'chip', label: 'LLM' },
      systemPrompt: {
        x: 92,
        y: 14,
        textIcon: '{}',
        label: 'System Prompt',
        branch: true,
        anchor: 'llm',
        callout: 'Always answer like a supply chain management professor.'
      }
    },
    start: 'computer',
    secondaryBalls: [{ id: 'sp', start: 'systemPrompt' }],
    path: [
      { type: 'move', to: 'llm' },
      { type: 'merge', to: 'llm', showCallout: 'systemPrompt', duration: MOVE_MS * 2 },
      { type: 'move', to: 'computer' }
    ]
  },
  'assistant-rag': {
    caption: 'The developer determines the workflow.',
    userMessage: 'What is in these documents?',
    response:
      "These documents cover Q2 sales performance, the Columbus Distribution Center's SOP, and a supplier performance summary.",
    nodes: {
      computer: { x: 8, y: 50, icon: 'computer', label: 'User' },
      junction: { x: 50, y: 50, invisible: true },
      kb: {
        x: 50,
        y: 16,
        icon: 'layers',
        label: 'Knowledge Base',
        branch: true,
        anchor: 'junction',
        callout: ['Q2 Sales Report', 'Columbus DC SOP', 'Supplier Perf. Summary']
      },
      llm: { x: 92, y: 50, icon: 'chip', label: 'LLM' }
    },
    start: 'computer',
    path: [
      { type: 'move', to: 'junction' },
      { type: 'move', to: 'kb', showCallout: true, duration: MOVE_MS * 2 },
      { type: 'move', to: 'junction' },
      { type: 'move', to: 'llm' },
      { type: 'move', to: 'computer' }
    ]
  },
  'agent-single-tool': {
    caption: 'The model can determine the next action.',
    userMessage: "What's the weather in Columbus?",
    response: "It's 68°F and partly cloudy in Columbus right now.",
    nodes: {
      computer: { x: 8, y: 50, icon: 'computer', label: 'User' },
      llm: {
        x: 40,
        y: 50,
        icon: 'chip',
        label: 'LLM',
        callout: 'ReAct loop: the model evaluates the situation and chooses the next action.'
      },
      tool: { x: 74, y: 50, icon: 'tool', label: 'Tool' },
      weather: { x: 74, y: 6, icon: 'cloud', label: 'weather.com', branch: true, anchor: 'tool' }
    },
    groupBoxes: [{ nodes: ['llm', 'tool'], label: 'Agent', pad: { left: 9, right: 9, top: 22, bottom: 26 } }],
    start: 'computer',
    path: [
      { type: 'move', to: 'llm' },
      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: 'ReAct loop: the model evaluates the situation and chooses the next action.'
      },
      { type: 'move', to: 'tool' },
      { type: 'move', to: 'weather' },
      { type: 'move', to: 'tool' },
      { type: 'move', to: 'llm' },
      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: 'ReAct loop: the model evaluates the result and chooses what to do next.'
      },
      { type: 'move', to: 'computer' }
    ]
  },
  'agent-harness': {
    caption: 'The harness gives the model memory, skills, and tools to call.',
    userMessage: "Show me last week's delayed shipments and email me a summary.",
    response:
      "Pulled 6 delayed shipments from last week, analyzed the causes, and emailed you the summary. I've also updated my memory with this week's numbers.",
    trackHeight: 560,
    mobileTrackHeight: 680,
    nodes: {
      computer: { x: 10, y: 50, icon: 'computer', label: 'User' },
      guardrailIn: { x: 25, y: 50, icon: 'shield', label: 'Guardrail Check' },
      systemPrompt: {
        x: 30,
        y: 10,
        textIcon: '{}',
        label: 'System Prompt',
        branch: true,
        skipConnector: true,
        callout: 'Always answer like a supply chain management professor.'
      },
      skill: { x: 50, y: 10, icon: 'book', label: 'Analysis Skill', branch: true, skipConnector: true },
      memory: { x: 70, y: 10, icon: 'save', label: 'Memory', branch: true, skipConnector: true },
      llm: { x: 50, y: 50, icon: 'chip', label: 'LLM', callout: 'Reasoning about the next step.' },
      toolCode: { x: 70, y: 50, icon: 'code', label: 'Code Execution' },
      toolDb: {
        x: 70,
        y: 34,
        icon: 'database',
        label: 'Database',
        branch: true,
        anchor: 'toolCode',
        skipConnector: true
      },
      toolEmail: { x: 70, y: 66, icon: 'mail', label: 'Email', branch: true, anchor: 'toolCode' },
      shipmentDb: {
        x: 92,
        y: 34,
        icon: 'database',
        label: 'Shipment Database',
        branch: true,
        anchor: 'toolDb',
        skipConnector: true
      },
      emailServer: {
        x: 92,
        y: 66,
        icon: 'mail',
        label: 'Email Server',
        branch: true,
        anchor: 'toolEmail',
        skipConnector: true
      }
    },
    groupBoxes: [
      {
        nodes: ['guardrailIn', 'systemPrompt', 'skill', 'memory', 'llm', 'toolCode', 'toolDb', 'toolEmail'],
        label: 'Agent',
        pad: { left: 7.5, right: 12, top: 8, bottom: 16 }
      },
      {
        nodes: ['toolCode', 'toolDb', 'toolEmail'],
        label: 'Tool Calling',
        style: 'solid',
        labelCenter: true,
        pad: { left: 9, right: 9, top: 8, bottom: 12 }
      }
    ],
    // The main connector line stops at the Tool Calling box's left border
    // (toolCode.x - its left pad, 70 - 9) instead of running through the box
    // to Code Execution's center — see mainLineEnd handling in InfraDemo.js.
    mainLineEnd: 61,
    start: 'computer',
    secondaryBalls: [
      { id: 'sp', start: 'systemPrompt' },
      { id: 'sk', start: 'skill' },
      { id: 'mem', start: 'memory' }
    ],
    path: [
      { type: 'move', to: 'guardrailIn' },
      { type: 'hold', at: 'guardrailIn', duration: 500 },
      { type: 'move', to: 'llm' },
      { type: 'merge', to: 'llm', duration: Math.round(MOVE_MS * 1.4), mergeAt: Math.round(MOVE_MS * 1.4) - 60 },

      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: "Reasoning: I need last week's delayed shipments.",
        cycleTools: { entries: ['toolDb', 'toolCode', 'toolEmail'], select: 'toolDb' }
      },
      { type: 'move', to: 'toolDb' },
      { type: 'move', to: 'shipmentDb' },
      { type: 'move', to: 'toolDb' },
      { type: 'move', to: 'llm' },

      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: 'Reasoning: analyzing the data using the Shipment Analysis Guide.',
        cycleTools: { entries: ['toolDb', 'toolCode', 'toolEmail'], select: 'toolCode' }
      },
      { type: 'move', to: 'toolCode' },
      { type: 'move', to: 'llm' },

      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: 'Reasoning: sending the summary by email.',
        cycleTools: { entries: ['toolDb', 'toolCode', 'toolEmail'], select: 'toolEmail' }
      },
      { type: 'move', to: 'toolEmail' },
      { type: 'move', to: 'emailServer' },
      { type: 'move', to: 'toolEmail' },
      { type: 'move', to: 'llm' },

      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: "Reasoning: updating memory with this week's results."
      },
      { type: 'move', to: 'memory' },
      { type: 'move', to: 'llm' },

      {
        type: 'spin',
        at: 'llm',
        showCallout: true,
        duration: SPIN_MS + 900,
        calloutText: 'Checking response before displaying to user.'
      },
      { type: 'move', to: 'computer' }
    ]
  }
};

export const SUBTAB_CONFIG = {
  assistant: [
    { id: 'system-prompt', label: 'System Prompt' },
    { id: 'rag', label: 'RAG' }
  ],
  agent: [
    { id: 'single-tool', label: 'Single Tool Call' },
    { id: 'harness', label: 'Agent Harness' }
  ]
};

export function scenarioKey(topTab, variant) {
  return SUBTAB_CONFIG[topTab] ? `${topTab}-${variant}` : topTab;
}
